/**
 * @file Notes and tagged-text API helpers for parent-board detail views.
 */

import { saveTextFieldAndTagUsers as configuredSaveTextFieldAndTagUsers } from "../../app/firebase/callables.js";
import { appDataDetails as configuredAppDataDetails } from "../../app/state/state.js";
import { openConfirmationModal as configuredOpenConfirmationModal } from "../ui/confirmationModal.js";
import {
    formatContentForSave as configuredFormatContentForSave,
} from "../utils/contentUtils.js";
import {
    getCollectionName as configuredGetCollectionName,
    getItemType as configuredGetItemType,
} from "../utils/itemLookup.js";
import {
    getLongTextFieldName,
} from "../details/detailLongText.js";

/**
 * Creates notes API helpers.
 * @param {object} deps Dependency bag.
 * @returns {object} Notes API helpers.
 */
export function createNotesApi({
    appDataDetails,
    formatContentForSave,
    getCollectionName,
    getItemType,
    openConfirmationModal,
    saveTextFieldAndTagUsers,
}) {
    const resolveItemType = (itemId) => {
        let itemType = getItemType(itemId);
        if (!itemType && appDataDetails.tasks.find((task) => task.id === itemId)) {
            itemType = 'task';
        }
        return itemType;
    };

    /**
     * Saves note content through the tagging callable.
     * @param {object} params Note save parameters.
     * @param {string} params.itemId Parent item ID.
     * @param {string} params.rawText Raw note text.
     * @returns {Promise<void>}
     */
    const saveNoteContent = async ({ itemId, rawText }) => {
        const normalizedText = typeof rawText === 'string' ? rawText.trim() : '';
        const text = formatContentForSave(normalizedText);

        if (!itemId || !text) {
            throw new Error('Missing item ID or note text.');
        }

        const itemType = resolveItemType(itemId);
        if (!itemType) {
            throw new Error(`Could not determine item type for ID ${itemId}`);
        }

        const collectionName = getCollectionName(itemType);
        await saveTextFieldAndTagUsers({
            content: text,
            parentId: itemId,
            parentType: collectionName,
            saveMode: "notesSubcollection",
        });
    };

    /**
     * Adds a note through the tagging callable.
     * @param {Event} e Notes form submit event.
     * @returns {Promise<boolean>} Whether the note was saved.
     */
    const handleAddNote = async (e) => {
        e.preventDefault();
        const form = e.target;
        const itemId = form.dataset.itemId;
        const textInput = form.elements['note-text'];
        const saveNoteBtn = form.querySelector('#save-note-btn');
        const rawText = textInput.value.trim();

        if (!itemId || !rawText) {
            console.warn("handleAddNote: Missing item ID or note text.");
            return false;
        }

        const itemType = resolveItemType(itemId);
        if (!itemType) {
            console.error(`handleAddNote: Could not determine item type for ID ${itemId}`);
            return false;
        }

        let originalBtnText = 'Save Note';
        if (saveNoteBtn) {
            originalBtnText = saveNoteBtn.textContent;
            saveNoteBtn.textContent = 'Saving...';
            saveNoteBtn.disabled = true;
            saveNoteBtn.classList.add('opacity-70', 'cursor-wait');
        }

        try {
            await saveNoteContent({
                itemId,
                rawText,
            });

            textInput.value = '';

            if (saveNoteBtn) {
                saveNoteBtn.textContent = originalBtnText;
                saveNoteBtn.disabled = true;
                saveNoteBtn.classList.remove(
                    'opacity-70',
                    'cursor-wait',
                );
            }
            return true;
        } catch (error) {
            console.error(`Error calling saveTextFieldAndTagUsers for note:`, error);

            if (saveNoteBtn) {
                saveNoteBtn.textContent = originalBtnText;
                saveNoteBtn.disabled = false;
                saveNoteBtn.classList.remove('opacity-70', 'cursor-wait');
            }

            const errorMessage = (error.code === 'internal' || !error.message)
                ? 'Something went wrong. Please try again later.'
                : `Failed to add note: ${error.message}.`;
            openConfirmationModal('Error', errorMessage, async () => { }, false, 'OK');
            return false;
        }
    };

    /**
     * Saves a tagged details text field through the tagging callable.
     * @param {string} content Field content.
     * @param {string} itemId Item document ID.
     * @returns {Promise<void>}
     */
    const handleSaveComment = async (content, itemId) => {
        if (!itemId || content === null) {
            console.warn("handleSaveComment: Missing item ID or content.");
            return;
        }

        const finalContent = formatContentForSave(content);
        const itemType = resolveItemType(itemId);

        if (!itemType) {
            console.error(`handleSaveComment: Could not determine item type for ID ${itemId}`);
            openConfirmationModal(
                'Error',
                `Could not determine item type for ${itemId}.`,
                async () => { },
                false,
                'OK',
            );
            return;
        }

        const collectionName = getCollectionName(itemType);
        const fieldNameToUpdate = getLongTextFieldName(itemType);

        try {
            await saveTextFieldAndTagUsers({
                content: finalContent,
                parentId: itemId,
                parentType: collectionName,
                saveMode: "updateField",
                fieldName: fieldNameToUpdate,
            });
        } catch (error) {
            console.error(
                `Error calling saveTextFieldAndTagUsers for field '${fieldNameToUpdate}':`,
                error,
            );
            const errorMessage = (error.code === 'internal' || !error.message)
                ? 'Something went wrong. Please try again later.'
                : `Failed to save changes: ${error.message}.`;
            openConfirmationModal('Error', errorMessage, async () => { }, false, 'OK');
        }
    };

    return {
        handleAddNote,
        handleSaveComment,
        saveNoteContent,
    };
}

const configuredNotesApi = createNotesApi({
    appDataDetails: configuredAppDataDetails,
    formatContentForSave: configuredFormatContentForSave,
    getCollectionName: configuredGetCollectionName,
    getItemType: configuredGetItemType,
    openConfirmationModal: configuredOpenConfirmationModal,
    saveTextFieldAndTagUsers: configuredSaveTextFieldAndTagUsers,
});

export const handleAddNote = configuredNotesApi.handleAddNote;
export const handleSaveComment = configuredNotesApi.handleSaveComment;
export const saveNoteContent = configuredNotesApi.saveNoteContent;
